import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import Avatar from "@/components/common/Avatar";
import { WebIcons } from "@/config/antd";

const ProfilePicker = ({
  currentImage,
  onImageChange,
  className = "",
  bgColor = "secondary",
}) => {
  const [imageError, setImageError] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(currentImage);
  const fileInputRef = useRef(null);

  // Update preview when currentImage changes
  useEffect(() => {
    setPreviewUrl(currentImage);
    setImageError(false);
  }, [currentImage]);

  const handleClick = () => {
    fileInputRef.current.click();
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImageError(false);

    try {
      // Create URL for preview
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);

      // Call parent handler with file
      onImageChange(file, objectUrl);

      // Clean up the URL when component updates
      return () => URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error("Error creating preview:", error);
      setImageError(true);
    }
  };

  const handleImageError = () => {
    console.error("Image failed to load");
    setImageError(true);
  };

  return (
    // `group` lets the camera overlay respond to hover anywhere on the circle
    <div
      className={`relative group w-32 h-32 ${className}`}
      style={{ minWidth: "8rem", minHeight: "8rem" }}
      onClick={handleClick}>
      <input
        type="file"
        ref={fileInputRef}
        accept="image/*"
        onChange={handleImageChange}
        style={{ display: "none" }}
      />

      <div className={`w-full h-full rounded-full overflow-hidden border-2 border-gray-200 cursor-pointer bg-${bgColor}`}>
        <Avatar
          src={imageError ? "" : previewUrl}
          alt="Profile"
          size="full"
          className="!block !w-full !h-full"
          onError={handleImageError}
        />
      </div>

      {/* Camera overlay shown on hover to signal the picker is clickable */}
      <div
        className="absolute inset-0 flex items-center justify-center cursor-pointer
          rounded-full bg-black/50
          opacity-0 group-hover:opacity-100
          transition-opacity duration-200">
        <img
          src={WebIcons.Camera}
          alt="Upload photo"
          className="w-8 h-8"
          style={{ filter: "brightness(0) invert(1)" }}
        />
      </div>
    </div>
  );
};

ProfilePicker.propTypes = {
  currentImage: PropTypes.string,
  onImageChange: PropTypes.func,
  className: PropTypes.string,
  bgColor: PropTypes.string,
};

export default ProfilePicker;
